// src/utils/sessionManager.js
import API from "../api";

export const AUTH_NOTICE_KEY = "authNotice";

function getStoredToken() {
  return localStorage.getItem("token") || "";
}

function parseStoredJwt(token) {
  if (!token) return {};
  const parts = token.split(".");
  if (parts.length !== 3) return {};
  try {
    const pad = "=".repeat((4 - (parts[1].length % 4)) % 4);
    const b64 = (parts[1] + pad).replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(b64));
  } catch {
    return {};
  }
}

class SessionManager {
  constructor() {
    this.INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes idle logout
    this.REFRESH_BEFORE_EXPIRY = 5 * 60 * 1000; // Refresh JWT while user is still active
    this.CHECK_INTERVAL = 15 * 1000; // Idle check interval (in-memory only)

    this.lastActivityTime = Date.now();
    this.checkIntervalId = null;
    this.isRefreshing = false;
    this.expiring = false;
    this.listenersAttached = false;
    this.throttledActivityHandler = null;
    this.activityEvents = ["mousedown", "keydown", "scroll", "touchstart"];
  }

  init() {
    if (!getStoredToken()) return;

    this.expiring = false;
    this.lastActivityTime = Date.now();
    this.setupActivityListeners();
    this.startPeriodicCheck();
  }

  setupActivityListeners() {
    if (this.listenersAttached) return;

    let throttleTimer = null;
    this.throttledActivityHandler = () => {
      if (!throttleTimer) {
        throttleTimer = setTimeout(() => {
          this.onUserActivity();
          throttleTimer = null;
        }, 500);
      }
    };

    this.activityEvents.forEach((event) => {
      window.addEventListener(event, this.throttledActivityHandler, { passive: true });
    });
    this.listenersAttached = true;
  }

  isInactive(now = Date.now()) {
    return now - this.lastActivityTime >= this.INACTIVITY_TIMEOUT;
  }

  onUserActivity() {
    if (this.expiring) return;
    if (!getStoredToken()) return;

    const now = Date.now();
    if (this.isInactive(now)) {
      this.expireSession("auth.sessionInactive");
      return;
    }

    this.lastActivityTime = now;
    this.refreshTokenIfNeeded();
  }

  expireSession(message) {
    if (this.expiring) return;
    this.expiring = true;
    sessionStorage.setItem(AUTH_NOTICE_KEY, message);
    this.destroy();

    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("role");
    localStorage.removeItem("activeRole");
    localStorage.removeItem("roles");

    if (!window.location.pathname.includes("/login")) {
      window.location.replace("/login");
    }
  }

  startPeriodicCheck() {
    if (this.checkIntervalId) {
      clearInterval(this.checkIntervalId);
    }

    this.checkIntervalId = setInterval(() => {
      this.checkIdleAndRefresh();
    }, this.CHECK_INTERVAL);
  }

  checkIdleAndRefresh() {
    if (this.expiring) return;
    if (!getStoredToken()) return;

    if (this.isInactive()) {
      this.expireSession("auth.sessionInactive");
      return;
    }

    this.refreshTokenIfNeeded();
  }

  getJwtExpiryMs() {
    const payload = parseStoredJwt(getStoredToken());
    return payload.exp ? Number(payload.exp) * 1000 : 0;
  }

  async refreshTokenIfNeeded() {
    const jwtExp = this.getJwtExpiryMs();
    if (!jwtExp || this.isRefreshing) return;

    const timeUntilJwtExpiry = jwtExp - Date.now();
    if (timeUntilJwtExpiry > this.REFRESH_BEFORE_EXPIRY) return;

    await this.refreshToken();
  }

  async refreshToken() {
    if (this.isRefreshing) return;

    try {
      this.isRefreshing = true;
      const currentToken = getStoredToken();
      if (!currentToken) return;

      const response = await API.post("/auth/refresh", { token: currentToken });

      if (response.data?.token) {
        localStorage.setItem("token", response.data.token);

        if (response.data.username) {
          localStorage.setItem(
            "user",
            JSON.stringify({
              username: response.data.username,
              role: response.data.role,
            }),
          );
        }
      }
    } catch {
      // 401 interceptor will handle hard logout if refresh fails
    } finally {
      this.isRefreshing = false;
    }
  }

  destroy() {
    if (this.checkIntervalId) {
      clearInterval(this.checkIntervalId);
      this.checkIntervalId = null;
    }

    if (this.listenersAttached && this.throttledActivityHandler) {
      this.activityEvents.forEach((event) => {
        window.removeEventListener(event, this.throttledActivityHandler, { passive: true });
      });
      this.listenersAttached = false;
      this.throttledActivityHandler = null;
    }

    this.lastActivityTime = Date.now();
  }
}

const sessionManager = new SessionManager();
export default sessionManager;
